import { INTEGRATION_STATUS } from "app/pages/sales-intelligence/pages/management/constants/constants";
import type { IntegrationStatusResponse } from "app/pages/sales-intelligence/sub-modules/management/components/types";
import { createStatePropertySelector } from "app/pages/workspace/sales/helpers";
import { createSelector } from "reselect";
import { selectManagementReducer } from "./managementSelectors";
import { calculateCrmListStatus } from "../../utils/crmList";
import { getActiveImportSettings } from "../../utils/utils";
import { CRMListObjectsType } from "../../types";

const selectHubspotReducer = createStatePropertySelector(selectManagementReducer("hubspotIntegration"));

const selectHubspotStatusReducer = createStatePropertySelector(selectHubspotReducer("crmConnectionStatus"));
const selectHubspotCrmStateInfo = selectHubspotReducer("crmStateInfo");

export const selectHubspotCompaniesCount = createSelector(
    selectHubspotCrmStateInfo,
    (status) => status.accountExportFieldsCount,
);

export const selectHubspotContactsCount = createSelector(
    selectHubspotCrmStateInfo,
    (status) => status.contactExportFieldsCount,
);

const selectHubspotLeadsCount = createSelector(selectHubspotCrmStateInfo, (status) => status.leadExportFieldsCount);

const selectHubspotCrmExportLimit = selectHubspotReducer("crmExportLimit");

export const selectHsCrmExportContactsLimit = createSelector(
    selectHubspotCrmExportLimit,
    (crmExportLimit) => crmExportLimit?.contactsLimit ?? Infinity,
);

export const selectHsCrmExportAccountsLimit = createSelector(
    selectHubspotCrmExportLimit,
    (crmExportLimit) => crmExportLimit?.accountsLimit ?? Infinity,
);

export const selectHubspotStatusObject = selectHubspotReducer("crmConnectionStatus");
const selectIsCurrentUserVerifiedByHsCrm = selectHubspotReducer("isCurrentUserVerifiedByCrm");

export const selectHubspotAccountsToggleState = createSelector(
    selectHubspotStatusObject,
    (srmInfo) => srmInfo.isExportEnabled,
);

export const selectHubspotContactsToggleState = createSelector(
    selectHubspotStatusObject,
    (srmInfo) => srmInfo.isContactExportEnabled,
);

const selectExcludeStatusFields = selectHubspotReducer("excludeStatusFields");
export const selectHsExcludeStatusFields = createSelector(selectExcludeStatusFields, (excludeFields) =>
    excludeFields ? excludeFields : [],
);

const selectHsContactsFields = createSelector(selectHubspotReducer("contactFields"), (contactFields) =>
    contactFields ? contactFields : [],
);

export const selectHsExcludeAccountFields = selectHubspotReducer("excludeAccountFields");

export const selectHsIsLoadingExcludeAccountFields = selectHubspotReducer("isLoadingExcludeAccountFields");

// Status fields
export const selectHubspotStatus = selectHubspotStatusReducer("syncStatus");
export const selectHusbpotSyncFailedReasonCode = selectHubspotStatusReducer("syncFailedReasonCode");
const selectHubspotDomainsCount = selectHubspotStatusReducer("domainsCount");
export const selectHubspotLastSynced = selectHubspotStatusReducer("lastSynced");
const selectHubspotOwnerFirstName = selectHubspotStatusReducer("ownerFirstName");
const selectHubspotOwnerOwnerLastName = selectHubspotStatusReducer("ownerLastName");
export const selectIsHubspotLeadsFilterEnabled = selectHubspotStatusReducer("isLeadsFilterEnabled");
export const selectIsHubspotExportEnabled = selectHubspotStatusReducer("isExportEnabled");

export const selectIsHsCrmListEnabled = selectHubspotStatusReducer("isCrmListEnabled");
export const selectHsListObjectsType = selectHubspotStatusReducer("crmListObjectsType");

const selectHsListObjectTypes = createSelector(selectHsListObjectsType, (listObjectsType): CRMListObjectsType[] => {
    return listObjectsType === CRMListObjectsType.AccountsAndContacts
        ? [CRMListObjectsType.Accounts, CRMListObjectsType.Contacts]
        : [listObjectsType];
});
export const selectIsHsCrmLabelEnabled = selectHubspotStatusReducer("isCrmLabelsEnabled");

export const selectIsHsPredefinedOnCreateUpdateClaimEnabled = selectHubspotReducer(
    "predefinedOnCreateUpdateClaimEnabled",
);

// States
export const selectHubspotLoadingState = selectHubspotReducer("isLoading");
const selectHubspotError = selectHubspotReducer("error");
export const selectHsIsLoadingExcludeStatusFields = selectHubspotReducer("isLoadingExcludeStatusFields");

const selectHsMappingFields = selectHubspotReducer("mappingFields");
const selectHsMappingFieldsError = selectHubspotReducer("mappingFieldsError");
const selectIsLoadingHsMappingFields = selectHubspotReducer("isLoadingMappingFields");
export const selectIsUpdatingHsFilterFields = selectHubspotReducer("isUpdatingFilterFields");
export const selectIsUpdatingHsStatusField = selectHubspotReducer("isUpdatingStatusField");

const selectHsIntegrationLeadFilterStatusError = selectHubspotReducer("exclusionError");

const selectHsIsIntegrationLeadFilterStatusLoading = selectHubspotReducer("isExclusionLoading");

const selectHsIsSynced = createSelector(selectHubspotStatus, (status) => status === INTEGRATION_STATUS.Succeeded);

export const selectHsIsNotIntegrated = createSelector(
    selectHubspotStatus,
    (status) => status === INTEGRATION_STATUS.NotIntegrated,
);

export const selectHsCurrentStatusField = createSelector(selectHsMappingFields, (mappingFields) => {
    if (mappingFields) {
        const filteredFields = mappingFields.filter((field) => field.metadata === "Type");
        return filteredFields.length ? filteredFields[0].externalField : "";
    }
});

export const selectHsFilterFieldNames = createSelector(selectHsMappingFields, (mappingFields) => {
    if (!mappingFields) {
        return [];
    }
    return mappingFields.filter((field) => field.metadata === "AccountListType").map((field) => field.externalField);
});

export const selectHsLabelCurrentStatusField = createSelector(
    selectHsCurrentStatusField,
    selectHsExcludeAccountFields,
    (currentStatusField, excludeAccountFields) => {
        if (!currentStatusField) {
            return "";
        }
        const accountField = excludeAccountFields.find(({ name }) => currentStatusField === name);
        return accountField?.label || "";
    },
);

export const selectHsIntegrationLeadFilterStatus = selectHubspotReducer("isExclusionOn");

export const selectHsIntegrationCrmStatus = createSelector(
    selectHubspotStatus,
    selectIsHsCrmListEnabled,
    selectIsCurrentUserVerifiedByHsCrm,
    calculateCrmListStatus,
);

export const selectActiveHSImportSettings = createSelector(
    selectIsHsCrmListEnabled,
    selectIsHubspotLeadsFilterEnabled,
    selectIsHsCrmLabelEnabled,
    getActiveImportSettings,
);

export const selectHubspotStatusResponse = createSelector(
    selectHubspotStatusObject,
    selectHubspotCrmStateInfo,
    selectHubspotCrmExportLimit,
    selectIsCurrentUserVerifiedByHsCrm,
    selectIsHsPredefinedOnCreateUpdateClaimEnabled,
    (
        crmConnectionStatus,
        crmStateInfo,
        crmExportLimit,
        isCurrentUserVerifiedByCrm,
        predefinedOnCreateUpdateClaimEnabled,
    ): IntegrationStatusResponse => ({
        crmConnectionStatus,
        crmStateInfo,
        crmExportLimit: crmExportLimit ?? undefined,
        isCurrentUserVerifiedByCrm,
        companyAsLeadExportClaimEnabled: false,
        predefinedOnCreateUpdateClaimEnabled,
    }),
);
